/**
 * Chip row showing the currently-attached context tags. Click the
 * × to remove a reference. Tags persist across messages for the
 * session (owned by the chat runtime store).
 *
 * Visual style ports the legacy `#chatContextChips` chips verbatim
 * from `_legacy/wd.html` lines 2015-2018:
 *   - Rounded 20px pill with a true-blue translucent fill + border.
 *   - Sky-coloured label, letter-spacing 1px, fs-label, weight 700.
 *   - `×` removal glyph (literal char, fs-base) is muted by default,
 *     turns red on hover.
 *
 * `refKey()` is also consumed by the parallel agent's `ReferencePicker`
 * (active-state ticks) — keep it exported.
 */

import type { XaesReference } from "../reference-resolver";
import { cn } from "@/lib/utils";

type Props = {
  references: XaesReference[];
  onRemove: (ref: XaesReference) => void;
  className?: string;
};

/** Stable key for a reference — used for React keys and equality. */
export function refKey(r: XaesReference): string {
  const id =
    r.assetId ??
    r.outputId ??
    (typeof r.phase === "number" ? `p${r.phase}` : "_");
  return `${r.kind}/${r.workshopId}/${id}`;
}

export function ContextChips({ references, onRemove, className }: Props) {
  if (references.length === 0) return null;
  return (
    <div
      className={cn("flex flex-wrap items-center", className)}
      style={{ gap: 5 }}
    >
      {references.map((r) => (
        <span
          key={refKey(r)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 8px",
            borderRadius: 20,
            background: "rgba(var(--accent-true-blue-rgb), 0.12)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.35)",
            color: "var(--accent-sky)",
            fontFamily: "Inter, sans-serif",
            fontSize: "var(--fs-label)",
            fontWeight: 700,
            letterSpacing: "1px",
            cursor: "default",
          }}
        >
          <span style={{ whiteSpace: "nowrap" }}>{r.label}</span>
          <button
            type="button"
            onClick={() => onRemove(r)}
            aria-label={`Remove reference ${r.label}`}
            className="clickable"
            style={{
              background: "none",
              border: 0,
              padding: 0,
              marginLeft: 1,
              fontSize: "var(--fs-base)",
              lineHeight: 1,
              color: "var(--text-muted)",
              cursor: "pointer",
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color =
                "var(--accent-red)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color =
                "var(--text-muted)";
            }}
          >
            {"×"}
          </button>
        </span>
      ))}
    </div>
  );
}
